import {
  Landmark,
  ShieldCheck,
  FileCheck2,
  Handshake,
  Building2,
  Wallet,
} from "lucide-react";
import { LOAN_DETAIL } from "@/constants/testIds";

/**
 * Renders a compact regional banking context panel: rails, regulators,
 * compliance framework, region-specific documents and guarantee schemes.
 */
export default function RegionContextCard({ region, dense = false }) {
  if (!region) return null;

  const rows = [
    { icon: Wallet, label: "Payment rails", items: region.payment_rails },
    { icon: Building2, label: "Account identifier", items: [region.primary_identifier_label] },
    { icon: ShieldCheck, label: "Regulators", items: region.regulators },
    { icon: Landmark, label: "Compliance", items: region.compliance_framework },
    { icon: FileCheck2, label: "Region-specific documents", items: region.regional_documents },
    { icon: Handshake, label: "Guarantee schemes", items: region.guarantee_schemes },
  ];

  return (
    <div
      data-testid={LOAN_DETAIL.regionCard}
      className="rounded-2xl border border-border bg-card p-6 lg:p-8"
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold">
            Regional banking context
          </p>
          <h3 className="mt-2 font-display text-2xl tracking-tight">
            {region.name}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            {region.blurb}
          </p>
        </div>
        <span className="hidden md:inline font-mono text-xs text-muted-foreground shrink-0">
          {region.short}
        </span>
      </div>

      <div
        className={`mt-6 grid gap-5 ${
          dense ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        }`}
      >
        {rows.map(({ icon: Icon, label, items }) => (
          <div key={label} className="space-y-2">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
              <Icon aria-hidden="true" className="w-3.5 h-3.5 text-primary" />
              {label}
            </div>
            <ul className="space-y-1 text-sm text-foreground/85 leading-relaxed">
              {items?.filter(Boolean).map((it) => (
                <li key={it} className="pl-3 -indent-3">
                  <span className="text-secondary mr-1">·</span>
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground font-mono">
        <span>
          Countries · {region.countries.slice(0, 4).join(", ")}
          {region.countries.length > 4 ? ` +${region.countries.length - 4}` : ""}
        </span>
        <span>Typical rate · {region.typical_rate_hint}</span>
      </div>
    </div>
  );
}
